import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { toast } from 'react-toastify';
import { BsCheckCircle, BsXCircle, BsPencil, BsSearch, BsEye } from 'react-icons/bs';
import { Link } from 'react-router-dom';

const Dealers = () => {
    const { t } = useTranslation();
    const [dealers, setDealers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingDealer, setEditingDealer] = useState(null); // Düzenlenen bayi
    const [editForm, setEditForm] = useState({});

    const fetchDealers = async () => {
        try {
            const res = await axios.get('/users/dealers');
            setDealers(res.data);
        } catch (error) {
            console.error('Bayiler yüklenemedi');
        }
    };

    useEffect(() => {
        fetchDealers();
    }, []);

    // Durum Değiştir (Aktif/Pasif)
    const handleStatusChange = async (id, currentStatus) => {
        try {
            await axios.put(`/users/dealers/${id}/status`, { is_active: !currentStatus });
            toast.success(`Bayi ${!currentStatus ? 'onaylandı' : 'pasife alındı'}.`);
            fetchDealers();
        } catch (error) {
            toast.error('İşlem başarısız.');
        }
    };

    // Düzenleme Modunu Aç
    const startEdit = (dealer) => {
        setEditingDealer(dealer.id);
        setEditForm({
            company_name: dealer.company_name,
            username: dealer.username,
            email: dealer.email,
            phone: dealer.phone,
            tax_number: dealer.tax_number,
            tax_office: dealer.tax_office
        });
    };

    // Düzenlemeyi Kaydet
    const saveEdit = async () => {
        try {
            await axios.put(`/users/dealers/${editingDealer}`, editForm);
            toast.success('Bayi bilgileri güncellendi.');
            setEditingDealer(null);
            fetchDealers();
        } catch (error) {
            toast.error('Güncelleme başarısız.');
        }
    };

    // Filtreleme
    const filteredDealers = dealers.filter(d => 
        (d.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (d.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (d.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <h2 className="m-0">{t('dealers.title')}</h2>
                <div className="input-group w-100 w-md-auto">
                    <span className="input-group-text bg-white"><BsSearch /></span>
                    <input 
                        type="text" 
                        className="form-control" 
                        placeholder={t('dealers.search_placeholder')} 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="card shadow-sm">
                {/* Desktop: Table View */}
                <div className="table-responsive d-none d-lg-block">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>{t('dealers.company_name')}</th>
                                <th>{t('dealers.contact')}</th>
                                <th>{t('dealers.tax_info')}</th>
                                <th>{t('dealers.status')}</th>
                                <th>{t('dealers.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDealers.map(dealer => (
                                <tr key={dealer.id} className={!dealer.is_active ? 'table-warning' : ''}>
                                    <td>
                                        {editingDealer === dealer.id ? (
                                            <input type="text" className="form-control form-control-sm mb-1" value={editForm.company_name} onChange={e => setEditForm({...editForm, company_name: e.target.value})} />
                                        ) : (
                                            <span className="fw-bold">{dealer.company_name}</span>
                                        )}
                                    </td>
                                    <td>
                                        {editingDealer === dealer.id ? (
                                            <>
                                                <input type="text" className="form-control form-control-sm mb-1" placeholder="Yetkili" value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value})} />
                                                <input type="text" className="form-control form-control-sm mb-1" placeholder="Email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                                                <input type="text" className="form-control form-control-sm" placeholder="Tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                                            </>
                                        ) : (
                                            <div className="small">
                                                <div><i className="bi bi-person me-1"></i> {dealer.username}</div>
                                                <div><i className="bi bi-envelope me-1"></i> {dealer.email}</div>
                                                <div><i className="bi bi-telephone me-1"></i> {dealer.phone}</div>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {editingDealer === dealer.id ? (
                                            <>
                                                <input type="text" className="form-control form-control-sm mb-1" placeholder="Vergi No" value={editForm.tax_number} onChange={e => setEditForm({...editForm, tax_number: e.target.value})} />
                                                <input type="text" className="form-control form-control-sm" placeholder="Vergi Dairesi" value={editForm.tax_office} onChange={e => setEditForm({...editForm, tax_office: e.target.value})} />
                                            </>
                                        ) : (
                                            <div className="small text-muted">
                                                {dealer.tax_number ? `${dealer.tax_number} / ${dealer.tax_office}` : '-'}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {dealer.is_active ? (
                                            <span className="badge bg-success">{t('dealers.active')}</span>
                                        ) : (
                                            <span className="badge bg-warning text-dark">{t('dealers.pending')}</span>
                                        )}
                                    </td>
                                    <td>
                                        {editingDealer === dealer.id ? (
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-success" onClick={saveEdit}>{t('settings.save')}</button>
                                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingDealer(null)}>{t('dealers.cancel')}</button>
                                            </div>
                                        ) : (
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className={`btn btn-sm ${dealer.is_active ? 'btn-outline-danger' : 'btn-success'}`}
                                                    onClick={() => handleStatusChange(dealer.id, dealer.is_active)}
                                                    title={dealer.is_active ? t('dealers.passive') : t('dealers.approve')}
                                                >
                                                    {dealer.is_active ? <BsXCircle /> : <BsCheckCircle />} {dealer.is_active ? t('dealers.passive') : t('dealers.approve')}
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => startEdit(dealer)}
                                                    title={t('dealers.edit')}
                                                >
                                                    <BsPencil />
                                                </button>
                                                <Link 
                                                    to={`/admin/dealers/${dealer.id}`}
                                                    className="btn btn-sm btn-info text-white"
                                                    title={t('dealers.details')}
                                                >
                                                    <BsEye />
                                                </Link>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredDealers.length === 0 && (
                                <tr><td colSpan="5" className="text-center p-4">{t('dealers.not_found')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile: Card View */}
            <div className="d-lg-none mt-3">
                {filteredDealers.map(dealer => (
                    <div key={dealer.id} className={`card mb-3 ${!dealer.is_active ? 'bg-warning-soft' : ''}`}>
                        {editingDealer === dealer.id ? (
                            <div className="card-body p-3">
                                <h5 className="mb-3 fw-bold">{t('dealers.edit_dealer')}</h5>
                                <div className="mb-2">
                                    <label className="form-label small">{t('dealers.company_name')}</label>
                                    <input type="text" className="form-control form-control-sm" value={editForm.company_name} onChange={e => setEditForm({...editForm, company_name: e.target.value})} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small">{t('dealers.authorized')}</label>
                                    <input type="text" className="form-control form-control-sm" value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value})} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small">{t('dealers.email')}</label>
                                    <input type="email" className="form-control form-control-sm" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small">{t('dealers.phone')}</label>
                                    <input type="text" className="form-control form-control-sm" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                                </div>
                                <div className="d-flex gap-2 mt-3">
                                    <button className="btn btn-sm btn-success flex-grow-1" onClick={saveEdit}>{t('settings.save')}</button>
                                    <button className="btn btn-sm btn-secondary flex-grow-1" onClick={() => setEditingDealer(null)}>{t('dealers.cancel')}</button>
                                </div>
                            </div>
                        ) : (
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="fw-bold text-dark">{dealer.company_name}</div>
                                    {dealer.is_active ? <span className="badge bg-success">{t('dealers.active')}</span> : <span className="badge bg-warning text-dark">{t('dealers.pending')}</span>}
                                </div>

                                <div className="small text-muted border-top pt-2">
                                    <div className="mb-1"><strong>{t('dealers.authorized')}:</strong> {dealer.username}</div>
                                    <div className="mb-1"><strong>{t('dealers.email')}:</strong> {dealer.email}</div>
                                    <div className="mb-1"><strong>{t('dealers.phone')}:</strong> {dealer.phone}</div>
                                    <div><strong>{t('dealers.tax_info')}:</strong> {dealer.tax_number ? `${dealer.tax_number} / ${dealer.tax_office}` : '-'}</div>
                                </div>

                                <div className="d-flex gap-2 mt-3 border-top pt-3">
                                    <button className={`btn btn-sm flex-grow-1 ${dealer.is_active ? 'btn-outline-danger' : 'btn-success'}`} onClick={() => handleStatusChange(dealer.id, dealer.is_active)}>
                                        {dealer.is_active ? <BsXCircle className="me-1"/> : <BsCheckCircle className="me-1"/>} 
                                        {dealer.is_active ? t('dealers.passive') : t('dealers.approve')}
                                    </button>
                                    <Link to={`/admin/dealers/${dealer.id}`} className="btn btn-sm btn-info text-white" title={t('dealers.details')}><BsEye /></Link>
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => startEdit(dealer)} title={t('dealers.edit')}><BsPencil /></button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {filteredDealers.length === 0 && (
                    <div className="text-center p-4 text-muted">{t('dealers.not_found')}</div>
                )}
            </div>
        </AdminLayout>
    );
};

export default Dealers;
